import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { AssignableUser, IssueReport, ReportStatus, STATUS_TO_ENUM } from '../models/reports';
import { IGenericJSON } from '../helpers/interface';

@Injectable({
  providedIn: 'root',
})
export class IssueReportService {
  private http: HttpClient = inject(HttpClient);
  /**
   * Fetches users reports
   * @returns {Observable<Post[]>}
   */
  getReports(): Observable<IssueReport[]> {
    return this.http
      .get<IGenericJSON[]>(`/api/issue-reports`)
      .pipe(map((data) => IssueReport.digestResponse(data)));
  }

  /**
   * Creates a new report.
   * @param {any} report - The report payload
   * @returns {Observable<Post>}
   */
  createReport(report: FormData): Observable<IssueReport> {
    return this.http
      .post<IGenericJSON>('api/issue-reports', report)
      .pipe(map((data) => IssueReport.digest(data)));
  }

  /**
   * Updates the status of a report (e.g. Acknowledged -> In Progress -> Resolved).
   * Requires the `reports.manage_status` permission server-side.
   * @param {number} id - The report id
   * @param {ReportStatus} status - The new display status
   * @returns {Observable<IssueReport>} the updated report
   */
  updateStatus(id: number, status: ReportStatus): Observable<IssueReport> {
    return this.http
      .patch<IGenericJSON>(`api/issue-reports/${id}/status`, { status: STATUS_TO_ENUM[status] })
      .pipe(map((data) => IssueReport.digest(data)));
  }

  /**
   * Fetches the staff users a report can be assigned to.
   * Requires the `reports.assign` permission server-side.
   * @returns {Observable<AssignableUser[]>}
   */
  getAssignableUsers(): Observable<AssignableUser[]> {
    return this.http.get<AssignableUser[]>(`api/issue-reports/assignable-users`);
  }

  /**
   * Assigns a user to a report, or unassigns when `assignedTo` is null.
   * Requires the `reports.assign` permission server-side.
   * @param {number} id - The report id
   * @param {number | null} assignedTo - The user id to assign, or null to clear
   * @returns {Observable<IssueReport>} the updated report
   */
  assignUser(id: number, assignedTo: number | null): Observable<IssueReport> {
    return this.http
      .patch<IGenericJSON>(`api/issue-reports/${id}/assignee`, { assigned_to: assignedTo })
      .pipe(map((data) => IssueReport.digest(data)));
  }
}
